import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Stack, Tooltip, IconButton, FormGroup, FormControlLabel, Checkbox, Typography, Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import PageHeader from '../components/PageHeader';
import PermissionGate from '../components/PermissionGate';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../api';

// Fallback global-role catalog used only if the gateway catalog (/roles/global) can't be fetched,
// so the dialog still works. Normally the list comes from Auth0 via the gateway.
const FALLBACK_GLOBAL_ROLES = [
  { name: 'global_user', label: 'global_user' },
  { name: 'global_admin', label: 'global_admin' }
];

const emptyForm = { email: '', name: '', globalRoles: [], localRoleIds: [] };

const UsersTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [localRoles, setLocalRoles] = useState([]); // product roles catalog (from /roles)
  const [globalCatalog, setGlobalCatalog] = useState(FALLBACK_GLOBAL_ROLES); // from gateway /roles/global
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [editing, setEditing] = useState(null); // user_id when editing
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setRows(data.users);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to load users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Role catalogs for the pickers: local (product) roles + global roles (from the gateway/Auth0).
  useEffect(() => {
    (async () => {
      try {
        const [localRes, globalRes] = await Promise.all([
          api.get('/roles'),
          api.get('/roles/global')
        ]);
        setLocalRoles(localRes.data.roles || []);
        const gr = globalRes.data.roles || [];
        if (gr.length) {
          setGlobalCatalog(
            gr.map((r) => ({ name: r.name, label: r.description ? `${r.description} (${r.name})` : r.name }))
          );
        }
      } catch (err) {
        enqueueSnackbar(err.response?.data?.message || 'Failed to load roles', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    setEditing(row.user_id);
    setForm({ email: row.email, name: row.name || '', globalRoles: [], localRoleIds: [] });
    setFormOpen(true);
    // Pre-fill current global + local roles.
    setRolesLoading(true);
    try {
      const { data } = await api.get(`/users/${encodeURIComponent(row.user_id)}/roles`);
      setForm((f) => ({ ...f, globalRoles: data.global_roles || [], localRoleIds: data.local_role_ids || [] }));
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to load user roles', { variant: 'error' });
    } finally {
      setRolesLoading(false);
    }
  };

  const toggleGlobal = (name) =>
    setForm((f) => ({
      ...f,
      globalRoles: f.globalRoles.includes(name)
        ? f.globalRoles.filter((r) => r !== name)
        : [...f.globalRoles, name]
    }));

  const toggleLocal = (rid) =>
    setForm((f) => ({
      ...f,
      localRoleIds: f.localRoleIds.includes(rid)
        ? f.localRoleIds.filter((x) => x !== rid)
        : [...f.localRoleIds, rid]
    }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${encodeURIComponent(editing)}`, {
          email: form.email, name: form.name, globalRoles: form.globalRoles, localRoleIds: form.localRoleIds
        });
        enqueueSnackbar('User updated', { variant: 'success' });
      } else {
        const { data } = await api.post('/users', {
          email: form.email, name: form.name, globalRoles: form.globalRoles, localRoleIds: form.localRoleIds
        });
        const result = data.data || {};
        let message = 'User invited (reset email sent) — becomes active on first login';
        if (result.status === 'already_existed') {
          const others = result.existing_organizations || [];
          message = others.length
            ? `User already exists in ${others.join(', ')} — linked to your organization (no reset email sent)`
            : 'User already existed — linked to your organization (no reset email sent)';
        }
        enqueueSnackbar(message, { variant: 'success' });
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    setDeactivating(true);
    try {
      await api.delete(`/users/${encodeURIComponent(deactivateTarget.user_id)}`);
      enqueueSnackbar('User deactivated', { variant: 'success' });
      setDeactivateTarget(null);
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Deactivate failed', { variant: 'error' });
    } finally {
      setDeactivating(false);
    }
  };

  const activate = async (row) => {
    try {
      await api.post(`/users/${encodeURIComponent(row.user_id)}/activate`);
      enqueueSnackbar('User reactivated', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Activate failed', { variant: 'error' });
    }
  };

  const deleteLocal = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${encodeURIComponent(deleteTarget.user_id)}/local`);
      enqueueSnackbar('User deleted from product database', { variant: 'success' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
    { field: 'email', headerName: 'Email', flex: 1.3, minWidth: 200 },
    { field: 'user_id', headerName: 'User ID', flex: 1.4, minWidth: 220 },
    {
      field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => {
        const color = p.value === 'active' ? 'success' : p.value === 'invited' ? 'warning' : 'default';
        return (
          <Chip
            size="small"
            label={p.value}
            color={color}
            variant={p.value === 'active' ? 'filled' : 'outlined'}
          />
        );
      }
    },
    {
      field: 'actions', headerName: 'Actions', width: 160, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction="row">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          {p.row.status === 'deactivated' ? (
            <Tooltip title="Activate (re-add to this organization)">
              <IconButton size="small" color="success" onClick={() => activate(p.row)}>
                <LockOpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Deactivate (remove from this organization)">
              <IconButton size="small" color="error" onClick={() => setDeactivateTarget(p.row)}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete from product DB only">
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(p.row)}>
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Users in your current organization. Managed via the User Sync API (M2M); block deactivates upstream, the trash icon removes from this product's DB only."
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New user</Button>
          </Stack>
        }
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.user_id}
          loading={loading}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onClose={saving ? undefined : () => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit user' : 'New user'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email" type="email" fullWidth value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              label="Name" fullWidth value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <Divider />
            <Box>
              <Typography variant="overline" color="text.secondary">Global roles (Auth0)</Typography>
              <FormGroup>
                {globalCatalog.map((r) => (
                  <FormControlLabel
                    key={r.name}
                    control={
                      <Checkbox
                        size="small"
                        checked={form.globalRoles.includes(r.name)}
                        onChange={() => toggleGlobal(r.name)}
                        disabled={rolesLoading}
                      />
                    }
                    label={r.label}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">Local roles (this organization)</Typography>
              {localRoles.length ? (
                <FormGroup>
                  {localRoles.map((r) => (
                    <FormControlLabel
                      key={r.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.localRoleIds.includes(r.id)}
                          onChange={() => toggleLocal(r.id)}
                          disabled={rolesLoading}
                        />
                      }
                      label={`${r.display_name} (${r.name})`}
                    />
                  ))}
                </FormGroup>
              ) : (
                <Typography variant="body2" color="text.disabled">No product roles defined yet.</Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.email || !form.name}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate user in this organization?"
        message={`This removes ${deactivateTarget?.email} from your current organization (in Auth0). Their access to any other organizations is unaffected, and they can be reactivated here.`}
        confirmLabel="Deactivate"
        confirmColor="error"
        loading={deactivating}
        onConfirm={deactivate}
        onClose={() => setDeactivateTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete from product database?"
        message={`This removes ${deleteTarget?.email} from this product's database only. The account still exists in the SSO gateway and Auth0 — it is not deactivated or deleted upstream.`}
        confirmLabel="Delete locally"
        confirmColor="error"
        loading={deleting}
        onConfirm={deleteLocal}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

// Gate the whole page behind users:manage
const Users = () => (
  <PermissionGate permission="users:manage">
    <UsersTable />
  </PermissionGate>
);

export default Users;
